/**
 * Minimum order qty on PDP and cart quantity inputs.
 * PDP (when not tier-locked): allow typing above min; validate on blur/submit with
 * the same message as cart. Tier-locked products keep nearest-allowed clamping.
 */
(function () {
  function getMin(input) {
    var min = Number(input.getAttribute('data-min-qty') || input.min || 1);
    return Number.isFinite(min) && min >= 1 ? min : 0;
  }

  function getMax(input) {
    var max = Number(input.getAttribute('data-max-qty') || input.max || 0);
    return Number.isFinite(max) && max > 0 ? max : 0;
  }

  function getAllowed(input) {
    var raw = input.getAttribute('data-allowed-qtys');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return null;
      return parsed
        .map(function (n) { return Number(n); })
        .filter(function (n) { return Number.isFinite(n) && n > 0; })
        .sort(function (a, b) { return a - b; });
    } catch (e) {
      return null;
    }
  }

  function isQtyLocked(input) {
    return input.dataset.qtyLocked === 'true' || Boolean(getAllowed(input));
  }

  function nearestAllowed(qty, allowed) {
    var best = allowed[0];
    var bestDist = Math.abs(qty - best);
    for (var i = 1; i < allowed.length; i++) {
      var dist = Math.abs(qty - allowed[i]);
      if (dist < bestDist || (dist === bestDist && allowed[i] > best)) {
        best = allowed[i];
        bestDist = dist;
      }
    }
    return best;
  }

  function getFormErrorEl(input) {
    var productForm = input.closest('product-form');
    return productForm ? productForm.querySelector('.js-form-error') : null;
  }

  function showPdpQtyError(input, min) {
    var errorMsg = getFormErrorEl(input);
    if (!errorMsg) return;
    var template = (window.theme && theme.strings && theme.strings.cartMinQuantityError)
      || 'Minimum order quantity is [quantity].';
    errorMsg.textContent = template.replace('[quantity]', String(min));
    errorMsg.hidden = false;
  }

  function clearPdpQtyError(input) {
    var errorMsg = getFormErrorEl(input);
    if (!errorMsg) return;
    errorMsg.hidden = true;
    errorMsg.textContent = '';
  }

  function clampInput(input, opts) {
    var min = getMin(input);
    var max = getMax(input);
    var allowed = getAllowed(input);
    if (min < 1 && max < 1 && !allowed) return false;

    var value = Number(input.value);
    var next = value;
    if (!Number.isFinite(value) || value < 1) {
      next = min > 0 ? min : 1;
    } else if (min > 0 && value < min) {
      next = min;
    }
    if (max > 0 && next > max) {
      next = max;
    }
    if (allowed) {
      next = nearestAllowed(next, allowed);
    }

    if (next !== value) {
      input.value = String(next);
      if (!opts || opts.dispatch !== false) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }
    return false;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {{ showError?: boolean }} opts
   * @returns {boolean} true when qty is acceptable for add-to-cart / cart update
   */
  function validateQuantityInput(input, opts) {
    if (!input) return true;
    opts = opts || {};

    if (isQtyLocked(input)) {
      clampInput(input, { dispatch: false });
      return true;
    }

    var min = getMin(input);
    var max = getMax(input);
    var value = Number(input.value);

    if (!Number.isFinite(value) || value < 1) {
      var fallback = min > 0 ? min : 1;
      input.value = String(fallback);
      if (opts.showError && min > 0) showPdpQtyError(input, min);
      return false;
    }

    if (max > 0 && value > max) {
      input.value = String(max);
      return true;
    }

    if (min > 0 && value < min) {
      input.value = String(min);
      if (opts.showError) showPdpQtyError(input, min);
      return false;
    }

    clearPdpQtyError(input);
    return true;
  }

  window.theme = window.theme || {};

  theme.validateQuantityInput = validateQuantityInput;

  theme.validateQuantityInputsInForm = function (root) {
    var scope = root || document;
    var valid = true;
    scope.querySelectorAll('input[name="quantity"]').forEach(function (input) {
      if (!validateQuantityInput(input, { showError: true })) valid = false;
    });
    return valid;
  };

  theme.clampQuantityInputs = function (root) {
    var scope = root || document;
    scope.querySelectorAll('input[name="quantity"]').forEach(function (input) {
      if (isQtyLocked(input)) {
        clampInput(input, { dispatch: false });
      } else {
        validateQuantityInput(input, { showError: true });
      }
    });
    return theme.validateQuantityInputsInForm(scope);
  };

  function bind(input) {
    if (input.dataset.minOrderBound === '1') return;
    input.dataset.minOrderBound = '1';

    if (input.name === 'updates[]' && !isQtyLocked(input)) {
      return;
    }

    if (input.name === 'quantity' && !isQtyLocked(input)) {
      input.addEventListener('input', function () {
        clearPdpQtyError(input);
      });
      input.addEventListener('blur', function () {
        validateQuantityInput(input, { showError: true });
      });
      return;
    }

    clampInput(input, { dispatch: false });
    input.addEventListener('change', function () {
      clampInput(input);
    });
    input.addEventListener('blur', function () {
      clampInput(input);
    });
  }

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll(
      'input[name="quantity"][data-min-qty], input[name="quantity"][data-max-qty], '
      + 'input[name="quantity"][data-allowed-qtys], input[name="updates[]"][data-allowed-qtys]'
    ).forEach(bind);
    scope.querySelectorAll('input[name="updates[]"][data-min-qty], input[name="updates[]"][data-max-qty]').forEach(bind);
    scope.querySelectorAll('input[name="quantity"][min]').forEach(function (input) {
      var min = Number(input.min);
      if (Number.isFinite(min) && min > 1 && !input.hasAttribute('data-min-qty')) {
        input.setAttribute('data-min-qty', String(min));
      }
      bind(input);
    });
  }

  function updateColorGroupInputs(root, detail) {
    var scope = root || document;
    var variant = detail && detail.variant;
    var selectedOptions = (detail && detail.selectedOptions) || (variant && variant.options) || [];

    scope.querySelectorAll('[data-color-group-input]').forEach(function (input) {
      var position = Number(input.getAttribute('data-color-option-position') || 0);
      var color = '';

      if (position >= 1 && Array.isArray(selectedOptions) && selectedOptions.length >= position) {
        color = selectedOptions[position - 1] || '';
      } else if (Array.isArray(selectedOptions) && selectedOptions.length) {
        color = selectedOptions[0] || '';
      }

      if (color) input.value = color;
    });
  }

  document.addEventListener('on:variant:change', function (evt) {
    var picker = evt.target;
    var scope = (picker && picker.closest && picker.closest('.shopify-section')) || document;
    updateColorGroupInputs(scope, evt.detail || {});
    init(scope);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
